(() => {
  if (customElements.get('kr-cart-confirmation')) return;
  class KrCartConfirmation extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-kr-pdp]');
      this.form = this.root?.querySelector('.kr-pdp__form');
      this.dialog = this.querySelector('dialog');
      this.error = this.querySelector('[data-kr-cart-error]');
      this.closeButton = this.querySelector('[data-kr-cart-close]');
      if (!this.root || !this.form || !this.dialog || !this.error || !this.closeButton || !this.dialog.showModal || typeof fetch !== 'function') return;
      if (this.root.krCartConfirmation?.isConnected && this.root.krCartConfirmation !== this) return;
      try {
        this.addUrl = new URL(this.dataset.addUrl, location.href);
        this.cartUrl = new URL(this.dataset.cartUrl, location.href);
        if ([this.addUrl, this.cartUrl].some(url => url.origin !== location.origin) || !/\/cart\/add\.js$/.test(this.addUrl.pathname) || !/\/cart\.js$/.test(this.cartUrl.pathname)) return;
      } catch (_) { return; }
      this.root.krCartConfirmation = this;
      this.listeners?.abort();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.form.addEventListener('submit', event => this.add(event), options);
      this.closeButton.addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) { const rect = this.dialog.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close(); } }, options);
      this.dialog.addEventListener('close', () => this.restore(), options);
    }
    disconnectedCallback() {
      this.listeners?.abort();
      this.operation?.controller.abort();
      clearTimeout(this.operation?.timeout);
      this.operation = null;
      if (this.isPending) this.pending(false);
      if (this.root?.krCartConfirmation === this) delete this.root.krCartConfirmation;
      if (this.dialog?.open) this.dialog.close();
      this.restore();
    }
    pending(value) {
      if (this.isPending === value) return;
      this.isPending = value;
      this.root.dataset.krPurchasePending = String(value);
      if (value) {
        this.controls = [...this.root.querySelectorAll('.kr-pdp__form button, .kr-pdp__form input, .kr-pdp__form select, .kr-pdp__form textarea, [data-kr-option], [data-kr-sticky-add], [data-kr-sticky-option], [data-kr-sticky-quantity], [data-kr-sticky-minus], [data-kr-sticky-plus]')].map(control => ({ control, disabled: control.disabled }));
        this.controls.forEach(({ control }) => { control.disabled = true; });
        this.buttons = [...this.root.querySelectorAll('[data-kr-add], [data-kr-sticky-add]')].map(button => ({ button, text: button.textContent, busy: button.getAttribute('aria-busy') }));
        this.buttons.forEach(({ button }) => { button.textContent = this.dataset.pendingLabel; button.setAttribute('aria-busy', 'true'); });
      } else {
        this.controls?.forEach(({ control, disabled }) => { control.disabled = disabled; });
        this.buttons?.forEach(({ button, text, busy }) => { button.textContent = text; if (busy === null) button.removeAttribute('aria-busy'); else button.setAttribute('aria-busy', busy); });
      }
      this.root.dispatchEvent(new CustomEvent('kr:purchase-pending'));
    }
    showError(message) { this.error.querySelector('span').textContent = message; this.error.hidden = false; this.error.focus(); }
    money(cents, currency) {
      if (!Number.isFinite(cents) || cents < 0) return '';
      try { return new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: currency || this.dataset.currency || 'USD' }).format(Number(cents) / 100); }
      catch (_) { return `${(Number(cents) / 100).toFixed(2)} ${currency || this.dataset.currency || 'USD'}`; }
    }
    renderItems(items, requested, currency) {
      const container = this.querySelector('[data-kr-cart-added-items]');
      container.replaceChildren();
      items.forEach(item => {
        const row = document.createElement('article'); row.className = 'kr-cart-confirmation__item';
        const image = document.createElement('img'); image.width = 86; image.height = 86; image.alt = item.product_title || item.title || '';
        const source = item.featured_image?.url || item.image;
        if (source) { try { const url = new URL(source, location.href); if (url.protocol === 'https:') { url.searchParams.set('width', '180'); image.src = url.href; } } catch (_) {} }
        const info = document.createElement('div'); info.className = 'kr-cart-confirmation__item-info';
        const title = document.createElement('p'); title.className = 'kr-cart-confirmation__item-title'; title.textContent = item.product_title || item.title || '';
        const variant = document.createElement('p'); variant.className = 'kr-cart-confirmation__variant'; variant.textContent = item.variant_title === 'Default Title' ? '' : item.variant_title || '';
        const price = document.createElement('div'); price.className = 'kr-cart-confirmation__price-row';
        const amount = document.createElement('strong'); amount.textContent = this.money(item.final_price ?? item.price, currency);
        const quantity = document.createElement('span'); const added = requested.find(entry => String(entry.id) === String(item.variant_id || item.id)); quantity.textContent = `×${added?.quantity || 1}`;
        price.append(amount, quantity); info.append(title, variant, price); row.append(image, info); container.append(row);
      });
    }
    open(returnFocus) {
      if (this.dialog.open) return;
      this.returnFocus = returnFocus;
      this.locks = [...new Set([document.documentElement, document.body, ...document.querySelectorAll('.page-wrapper')])].map(element => ({
        element,
        overflow: ['overflow', 'overflow-x', 'overflow-y'].map(property => ({property, value:element.style.getPropertyValue(property), priority:element.style.getPropertyPriority(property)})),
      }));
      this.locks.forEach(({element}) => element.style.setProperty('overflow', 'hidden', 'important'));
      const header = document.querySelector('.kr-header')?.getBoundingClientRect();
      this.dialog.style.setProperty('--kr-cart-top', `${Math.max(16, Math.min(header?.bottom || 100, 180))}px`);
      try { this.dialog.showModal(); } catch (error) { this.restore(); throw error; }
      this.closeButton.focus({preventScroll:true});
    }
    restore() {
      this.locks?.forEach(({element, overflow}) => {
        overflow.forEach(({property}) => element.style.removeProperty(property));
        overflow.forEach(({property, value, priority}) => { if (value) element.style.setProperty(property, value, priority); });
      });
      this.locks = null;
      if (this.returnFocus?.isConnected) this.returnFocus.focus({preventScroll:true});
      this.returnFocus = null;
    }
    async add(event) {
      if (event.defaultPrevented || event.target !== this.form) return;
      event.preventDefault();
      if (this.root.dataset.krPurchasePending === 'true') return;
      const option = this.root.querySelector('[data-kr-variant]')?.selectedOptions[0];
      const input = this.root.querySelector('[data-kr-quantity]');
      const mainButton = this.root.querySelector('[data-kr-add]');
      const integer = number => Number.isSafeInteger(number) && number > 0;
      const id = Number(option?.value), quantity = Number(input?.value);
      if (this.root.dataset.live !== 'true' || !mainButton || mainButton.disabled || !option || option.disabled || option.dataset.available !== 'true' || !integer(id)) return;
      if (!integer(quantity)) { this.form.reportValidity(); return; }
      if (!this.form.checkValidity()) { this.form.reportValidity(); return; }
      const main = { id, quantity };
      const fields = new FormData(this.form);
      const properties = Object.create(null);
      for (const [key, value] of fields) { const match = key.match(/^properties\[(.+)\]$/); if (match && typeof value === 'string') properties[match[1]] = value; }
      if (Object.keys(properties).length) main.properties = properties;
      if (fields.get('selling_plan')) main.selling_plan = fields.get('selling_plan');
      const requested = [main];
      const addon = this.root.querySelector('[data-kr-addon-check]');
      if (addon?.checked) {
        const selected = this.root.querySelector('[data-kr-addon-variant]')?.selectedOptions[0];
        if (addon.disabled || !selected || selected.disabled || selected.dataset.available !== 'true' || !integer(Number(selected.value))) { this.showError(this.dataset.errorLabel); return; }
        requested.push({id:Number(selected.value),quantity:1});
      }
      this.error.hidden = true;
      this.operation?.controller.abort();
      clearTimeout(this.operation?.timeout);
      const operation = { controller:new AbortController(), returnFocus:document.activeElement, confirmed:false };
      this.operation = operation;
      const current = () => this.operation === operation && this.isConnected;
      this.pending(true);
      operation.timeout = setTimeout(() => operation.controller.abort(), 20000);
      try {
        const response = await fetch(this.addUrl.href, {method:'POST', credentials:'same-origin', signal:operation.controller.signal, headers:{'Content-Type':'application/json',Accept:'application/json'}, body:JSON.stringify({items:requested})});
        const result = await response.json();
        if (!current()) return;
        if (!response.ok || result.status) { this.showError(typeof result.description === 'string' ? result.description : this.dataset.errorLabel); return; }
        const items = Array.isArray(result.items) ? result.items : [result];
        if (!items.length || items.some(item => !item.id)) throw new Error('Unexpected cart response');
        operation.confirmed = true;
        this.renderItems(items, requested);
        this.querySelector('[data-kr-cart-subtotal-row]').hidden = true;
        this.querySelector('[data-kr-cart-savings-row]').hidden = true;
        this.pending(false);
        this.open(operation.returnFocus);
        clearTimeout(operation.timeout);
        operation.timeout = setTimeout(() => operation.controller.abort(), 8000);
        try {
          const cartResponse = await fetch(this.cartUrl.href,{credentials:'same-origin',cache:'no-store',signal:operation.controller.signal,headers:{Accept:'application/json'}});
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            if (!current() || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0 || !Number.isFinite(cart.total_price) || cart.total_price < 0) return;
            const refreshedItems = items.map(item => cart.items.find(line => line.key && line.key === item.key) || item);
            this.renderItems(refreshedItems, requested, cart.currency);
            document.querySelectorAll('.kr-cart-count, [ref="cartBubbleCount"]').forEach(element => {element.textContent = String(cart.item_count);element.classList.remove('hidden');});
            document.querySelectorAll('.cart-bubble').forEach(element => element.classList.toggle('visually-hidden', !cart.item_count));
            this.querySelector('[data-kr-cart-subtotal]').textContent = this.money(cart.total_price, cart.currency);
            this.querySelector('[data-kr-cart-subtotal-row]').hidden = false;
            if (cart.total_discount > 0) { this.querySelector('[data-kr-cart-savings]').textContent = this.money(cart.total_discount, cart.currency); this.querySelector('[data-kr-cart-savings-row]').hidden = false; }
            document.dispatchEvent(new CustomEvent('kr:cart-updated', {detail:{cart}}));
          }
        } catch (_) {}
      } catch (_) { if (current() && !operation.confirmed) this.showError(this.dataset.uncertainLabel); }
      finally { clearTimeout(operation.timeout); if (current() && this.isPending) this.pending(false); }
    }
  }
  customElements.define('kr-cart-confirmation', KrCartConfirmation);
})();
